import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Loader2, Sparkles, Layers, Palette } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  {
    key: "seats",
    label: "How many 'Seats' does your creative team currently require for licensing?",
    options: ["1-5", "5-15", "15-50", "50+"],
  },
  {
    key: "monthly_spend",
    label: "What is your estimated monthly spend on stock media licenses?",
    options: ["<$500", "$500-$2,000", "$2,000-$5,000", "$5,000+"],
  },
  {
    key: "problem",
    label: "Which of these workflow problems do you want EliteStock to solve first?",
    options: [
      "Client Approval Delays",
      "Legal/License Compliance",
      "Managing Multiple Subscriptions",
      "Brand Inconsistency",
    ],
  },
  {
    key: "primary_use",
    label: "How will these assets primarily be used?",
    options: [
      "Global Ad Campaigns",
      "Social Media Management",
      "Corporate Web/B2B",
      "Editorial/News Storytelling",
    ],
  },
  {
    key: "current_provider",
    label: "Which stock provider do you currently use for your primary campaigns?",
    options: ["Getty", "Shutterstock", "Adobe Stock", "Other"],
  },
  {
    key: "launch_timeline",
    label: "When is your next major campaign launch scheduled?",
    options: ["Within 1 month", "1-3 months", "3-6 months", "Not scheduled"],
  },
];

const FEATURES = [
  {
    key: "success-indicator",
    icon: Sparkles,
    title: "Success Indicator",
    desc: "Predictive performance scoring on every asset. See which visuals will convert — before you spend a single license credit.",
  },
  {
    key: "syncboards",
    icon: Layers,
    title: "SyncBoards™",
    desc: "One link. One board. Client review, threaded comments, and instant approvals converge into a single, brand-safe workspace.",
    image: "/syncboard.jpeg",
  },
  {
    key: "style-mood-boards",
    icon: Palette,
    title: "Style Mood Boards",
    desc: "Drop in a mood board. EliteStock intelligently filters the entire library to match its vibe, palette, and directorial tone.",
  },
];

export default function PreRegistration() {
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [form, setForm] = useState({
    agency_name: "",
    email: "",
    seats: "",
    monthly_spend: "",
    problem: "",
    primary_use: "",
    current_provider: "",
    launch_timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const valid = Object.values(form).every((v) => (typeof v === "string" ? v.trim().length > 0 : !!v));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/preregistrations`, form);
      setSubmitted(true);
      // scroll to top of card
      setTimeout(() => document.querySelector('[data-testid="prereg-success"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : d?.error) || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const parallax = Math.min(scrollY * 0.15, 120);

  return (
    <div className="min-h-screen bg-ink text-ivory selection:bg-gold selection:text-ivory">
      {/* Minimal top bar — no app navigation */}
      <header data-testid="prereg-topbar" className="fixed top-0 inset-x-0 z-30 bg-ink/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-ivory text-xl tracking-wide">
            Elite<span className="text-gold">Stock</span>
          </Link>
          <a href="#pre-register" className="text-[11px] uppercase tracking-[0.3em] text-ivory/80 hover:text-gold">Request access</a>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section data-testid="prereg-hero" className="relative pt-16 min-h-[92vh] overflow-hidden">
        <div className="absolute inset-0" style={{ transform: `translate3d(0, ${parallax}px, 0)`, willChange: "transform" }} aria-hidden ref={heroRef}>
          <img src="/prereg-hero.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 animate-[preregFloat_18s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/60 to-ink" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,rgba(13,148,136,0.18)_0%,transparent_55%)]" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gold border border-gold/30 rounded-full px-3 py-1.5 mb-6 opacity-0 animate-[preregFade_1s_0.15s_ease-out_forwards]">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /> Private preview · Agencies only
            </div>
            <h1 data-testid="prereg-headline" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight opacity-0 animate-[preregFade_1s_0.35s_ease-out_forwards]">
              The full platform<br />
              for creative agencies<br />
              is <span className="italic text-gold">in the making.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ivory/70 max-w-2xl leading-relaxed opacity-0 animate-[preregFade_1s_0.55s_ease-out_forwards]">
              A licensing OS designed for the way modern agencies actually work — approvals, mood boards, and performance intelligence, unified. Reserve your team's seat before public launch.
            </p>
            <div className="mt-10 flex items-center gap-4 opacity-0 animate-[preregFade_1s_0.75s_ease-out_forwards]">
              <a href="#pre-register" data-testid="prereg-cta-hero" className="group inline-flex items-center gap-3 px-7 py-4 bg-gold text-ivory hover:bg-gold/90 transition-colors text-sm tracking-wide">
                Pre-register for early access
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <span className="text-xs text-ivory/50 hidden sm:inline">Invite-only · 2026 launch</span>
            </div>
          </div>
        </div>
        {/* CSS keyframes */}
        <style>{`
          @keyframes preregFade { to { opacity: 1; transform: translateY(0); } from { opacity: 0; transform: translateY(14px); } }
          @keyframes preregFloat { 0%,100% { transform: scale(1.02) translateY(0); } 50% { transform: scale(1.05) translateY(-14px); } }
        `}</style>
      </section>

      {/* ============ FEATURES ============ */}
      <section data-testid="prereg-features" className="relative bg-ink border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.35em] text-gold mb-3">What's coming</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">Three ideas that will redefine how your agency licenses.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <article key={f.key} data-testid={`prereg-feature-${f.key}`} className="group relative p-8 md:p-10 border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-gold/40 transition-colors overflow-hidden">
                  {f.image && (
                    <div className="absolute -right-8 -bottom-8 w-56 opacity-30 group-hover:opacity-45 transition-opacity">
                      <img src={f.image} alt="" className="w-full h-auto rounded-md" />
                    </div>
                  )}
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-6">
                      <Icon className="h-5 w-5 text-gold" />
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight">{f.title}</h3>
                    <p className="mt-4 text-ivory/70 text-[15px] leading-relaxed">{f.desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ PRE-REG FORM ============ */}
      <section id="pre-register" data-testid="prereg-form-section" className="relative bg-ink border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
          {!submitted && (
            <>
              <div className="mb-10 text-center">
                <div className="text-[11px] uppercase tracking-[0.35em] text-gold mb-3">Reserve your seat</div>
                <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">Pre-register your agency.</h2>
                <p className="mt-4 text-ivory/70">Answer 8 short questions. We'll be in touch as we roll out access.</p>
              </div>
              <form onSubmit={submit} data-testid="prereg-form" className="bg-gradient-to-br from-white/[0.04] to-transparent border border-white/10 p-8 md:p-10 space-y-8">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-ivory/70">What is the name of your Agency/Brand?</label>
                  <input
                    data-testid="prereg-agency-name"
                    type="text" required maxLength={200}
                    value={form.agency_name}
                    onChange={(e) => setF("agency_name", e.target.value)}
                    placeholder="e.g. Acme Creative Studio"
                    className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none px-1 py-3 text-lg text-ivory placeholder:text-ivory/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-ivory/70">Work email</label>
                  <input
                    data-testid="prereg-email"
                    type="email" required
                    value={form.email}
                    onChange={(e) => setF("email", e.target.value)}
                    placeholder="you@agency.com"
                    className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none px-1 py-3 text-lg text-ivory placeholder:text-ivory/30 transition-colors"
                  />
                </div>

                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] uppercase tracking-[0.3em] text-ivory/70">{f.label}</label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {f.options.map((opt) => {
                        const active = form[f.key] === opt;
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => setF(f.key, opt)}
                            data-testid={`prereg-${f.key}-${opt.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g,'').toLowerCase()}`}
                            className={`px-4 py-2.5 rounded-full text-sm tracking-wide border transition-all ${active ? "border-gold bg-gold text-ivory shadow-[0_0_20px_rgba(13,148,136,0.35)]" : "border-white/15 bg-white/[0.02] text-ivory/80 hover:border-gold/60 hover:text-ivory"}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {error && <div data-testid="prereg-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 p-3">{error}</div>}

                <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-white/10">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-ivory/50">
                    {valid ? "Ready to submit" : "Please complete all fields"}
                  </div>
                  <button
                    type="submit"
                    data-testid="prereg-submit"
                    disabled={!valid || submitting}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold text-ivory hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    {submitting ? "Submitting…" : "Request early access"}
                  </button>
                </div>
              </form>
            </>
          )}

          {submitted && (
            <div data-testid="prereg-success" className="text-center bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 p-10 md:p-14">
              <div className="mx-auto w-14 h-14 rounded-full bg-gold text-ivory flex items-center justify-center mb-6">
                <Check className="h-6 w-6" strokeWidth={3} />
              </div>
              <h3 className="font-serif text-3xl md:text-4xl">You're on the list.</h3>
              <p className="mt-4 text-ivory/70 max-w-lg mx-auto">
                We've received your application for <span className="text-gold">{form.agency_name}</span>. Our team will reach out at <span className="text-gold">{form.email}</span> when your agency's access window opens.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ivory/50">
          <div>© 2026 EliteStock. All rights reserved.</div>
          <div className="text-[10px] uppercase tracking-[0.35em]">Private preview build</div>
        </div>
      </footer>
    </div>
  );
}
